import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import {
  AuthFormValues,
  authInitialValues,
  authValidationSchema,
  FirstStepSignUpFormValues,
} from '../../utils/form-validation.ts';
import { StyledRadio } from './styled-radio.tsx';

const labelStyle = {
  color: 'white',
  fontSize: '14px',
  marginBottom: '4px',
};

interface SignupUserInfoProps {
  // Used when the user navigates back to the first step of the signup form from the second step.
  firstStepSignUpValues: FirstStepSignUpFormValues | undefined;
  setFirstStepSignUpValues: (val: FirstStepSignUpFormValues) => void;
  setActivePage: (val: string) => void;
}

export const SignupUserInfo = ({
  firstStepSignUpValues,
  setFirstStepSignUpValues,
  setActivePage,
}: SignupUserInfoProps) => {
  return (
    <Box data-testid="signup-form">
      <Typography sx={{ marginBottom: '12px', textAlign: 'center' }}>
        For expanded tool use, saved searches, favoriting, and more, create an
        account below:
      </Typography>
      <Formik
        initialValues={
          firstStepSignUpValues
            ? { ...authInitialValues, ...firstStepSignUpValues }
            : authInitialValues
        }
        validationSchema={authValidationSchema}
        validateOnBlur={true}
        validateOnMount={true}
        validateOnChange={true}
        onSubmit={(values) => {
          setFirstStepSignUpValues({
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            passwordConfirm: values.passwordConfirm,
            phone: values.phone,
            textOrEmailPreference: values.textOrEmailPreference,
          });
          setActivePage('auth2');
        }}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          setFieldValue,
          isValid,
          submitCount,
        }: FormikProps<AuthFormValues>) => (
          <Form>
            <>
              <Box className="field-group" data-testid="signup-form-page-1">
                <Box className="field">
                  <InputLabel sx={labelStyle}>First Name</InputLabel>
                  <TextField
                    className="text-input"
                    fullWidth
                    id="firstName"
                    name="firstName"
                    placeholder="First Name"
                    onChange={handleChange}
                    value={values.firstName}
                    error={touched.firstName && Boolean(errors.firstName)}
                  />
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Last Name</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name"
                    onChange={handleChange}
                    value={values.lastName}
                    error={touched.lastName && Boolean(errors.lastName)}
                  />
                </Box>
              </Box>
              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>Email</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    id="email"
                    name="email"
                    placeholder="Email"
                    onChange={handleChange}
                    value={values.email}
                    error={touched.email && Boolean(errors.email)}
                  />
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Cell Phone Number</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    id="phone"
                    name="phone"
                    placeholder="Cell Phone Number"
                    type="phone"
                    onChange={handleChange}
                    value={values.phone}
                    error={touched.phone && Boolean(errors.phone)}
                  />
                </Box>
              </Box>

              <Box
                className="field-group"
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Box>
                  <Typography>Preferred Contact Method</Typography>
                  <FormControl
                    error={
                      touched.textOrEmailPreference &&
                      Boolean(errors.textOrEmailPreference)
                    }
                  >
                    <RadioGroup
                      aria-labelledby="radio-buttons-group-label"
                      name="textOrEmailPreference"
                      value={values.textOrEmailPreference}
                      onChange={(event) => {
                        void setFieldValue(
                          'textOrEmailPreference',
                          event.currentTarget.value
                        );
                      }}
                    >
                      <FormControlLabel
                        value="SMS_TEXT"
                        control={StyledRadio()}
                        label="Text/SMS"
                      />
                      <FormControlLabel
                        value="EMAIL"
                        control={StyledRadio()}
                        label="Email"
                      />
                      <FormControlLabel
                        value="PHONE_CALL"
                        control={StyledRadio()}
                        label="Phone Call"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>Password</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    id="password"
                    name="password"
                    placeholder="Password"
                    type="password"
                    onChange={handleChange}
                    value={values.password}
                    error={touched.password && Boolean(errors.password)}
                  />
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Re-enter Password</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    id="passwordConfirm"
                    name="passwordConfirm"
                    placeholder="Re-enter password"
                    type="password"
                    onChange={handleChange}
                    value={values.passwordConfirm}
                    error={
                      touched.passwordConfirm && Boolean(errors.passwordConfirm)
                    }
                  />
                </Box>
              </Box>
              {submitCount > 1 && Object.values(errors).length > 0 && (
                <Box className="error-text" data-testid="errors">
                  <Typography sx={{ borderBottom: 'solid 1px white' }}>
                    Missing Required Fields:{' '}
                  </Typography>
                  <ul>
                    {Object.values(errors).map((error) => {
                      return <li key={error}>{error}</li>;
                    })}
                  </ul>
                </Box>
              )}
              <Button
                disabled={!isValid}
                type={'submit'}
                sx={{ minWidth: '100%' }}
              >
                Next
              </Button>
            </>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
